import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Calendar, User, Truck, Clock, MapPin, Coffee, Package, FileCsv,
  Printer, Path, FlagCheckered, SignIn, SignOut, Timer, Warning, X,
} from '@phosphor-icons/react';
import { GoogleMap, Polyline } from '@react-google-maps/api';
import { useGoogleMaps, GOOGLE_MAPS_MAP_ID } from '../../lib/googleMaps';
import AdvancedMarker from '../../components/maps/AdvancedMarker';

// ----- helpers -----
const fmtTime = (iso) => iso
  ? new Date(iso).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })
  : '—';
const fmtHm = (iso) => iso
  ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  : '—';
const fmtMin = (m) => m == null ? '—' : `${Math.round(m)} min`;
const todayStr = () => new Date().toISOString().slice(0, 10);

const MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
];

// ----- Trip Route Modal (read-only travelled route on Google Map) -----
function TripRouteModal({ tripId, onClose }) {
  const { isLoaded } = useGoogleMaps();
  const [data, setData] = useState(null);
  const mapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: d } = await api.get(`/trips/detail/${tripId}`);
        if (!cancelled) setData(d);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [tripId]);

  const trip = data?.trip;
  const gps = data?.gps_tracks || [];
  const path = gps.map(p => ({ lat: p.lat, lng: p.lng }));
  const center = path.length ? path[Math.floor(path.length / 2)]
    : (trip?.pickup_lat ? { lat: (trip.pickup_lat + trip.drop_lat) / 2, lng: (trip.pickup_lng + trip.drop_lng) / 2 } : { lat: 11.1085, lng: 77.3411 });

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={onClose} data-testid="trip-route-modal">
      <div className="bg-white w-full max-w-6xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E5] bg-[#0A0A0A] text-white">
          <div>
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#00C853]">Travelled Route</p>
            <p className="font-mono text-sm">#{tripId} {trip && <span className="text-[#999] ml-2">{trip.pickup_supplier_name} → {trip.drop_supplier_name}</span>}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1" data-testid="trip-route-close-btn"><X size={22} weight="bold" /></button>
        </div>
        <div className="flex-1 relative">
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={center}
              zoom={12}
              onLoad={(m) => {
                mapRef.current = m;
                if (path.length > 1 && window.google) {
                  const b = new window.google.maps.LatLngBounds();
                  path.forEach(p => b.extend(p));
                  if (trip?.pickup_lat) b.extend({ lat: trip.pickup_lat, lng: trip.pickup_lng });
                  if (trip?.drop_lat) b.extend({ lat: trip.drop_lat, lng: trip.drop_lng });
                  m.fitBounds(b, 60);
                }
              }}
              options={{ styles: MAP_STYLES, mapId: GOOGLE_MAPS_MAP_ID, mapTypeControl: false }}
            >
              {trip?.pickup_lat && trip?.pickup_lng && (
                <AdvancedMarker position={{ lat: trip.pickup_lat, lng: trip.pickup_lng }} icon={{ color: '#00C853', label: 'P', size: 32 }} title={`Pickup: ${trip.pickup_supplier_name || ''}`} />
              )}
              {trip?.drop_lat && trip?.drop_lng && (
                <AdvancedMarker position={{ lat: trip.drop_lat, lng: trip.drop_lng }} icon={{ color: '#FF3333', label: 'D', size: 32 }} title={`Drop: ${trip.drop_supplier_name || ''}`} />
              )}
              {path.length > 1 && (
                <Polyline path={path} options={{ strokeColor: '#FF3333', strokeWeight: 5, strokeOpacity: 0.9 }} />
              )}
            </GoogleMap>
          ) : (
            <div className="h-full flex items-center justify-center text-[#666]">Loading map…</div>
          )}
        </div>
        <div className="px-5 py-2 border-t border-[#E5E5E5] text-xs text-[#666] flex gap-4">
          <span>GPS points: <b>{gps.length}</b></span>
          {trip?.google_kms != null && <span>Google KMs: <b>{trip.google_kms}</b></span>}
          {trip?.manual_kms != null && <span>Manual KMs: <b>{trip.manual_kms}</b></span>}
        </div>
      </div>
    </div>
  );
}

// ----- Trip card -----
function TripCard({ trip, onOpenMap, isChild = false }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const dc = trip.dc_photos?.[0]?.url;
  const dcUrl = dc?.startsWith('/') ? `${API}${dc}` : dc;
  const items = trip.items || [];
  return (
    <div
      className={`border ${isChild ? 'border-[#A855F7]/30 bg-[#A855F7]/5 ml-6' : 'border-[#E5E5E5] bg-white'} p-3 cursor-pointer hover:shadow-md transition-shadow`}
      onClick={() => onOpenMap(trip.id)}
      data-testid={`daily-trip-card-${trip.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isChild ? (
              <span className="text-[9px] uppercase tracking-widest bg-[#A855F7] text-white px-1.5 py-0.5 font-bold">↳ Pooled</span>
            ) : (
              <Truck size={14} className="text-[#002FA7]" />
            )}
            <span className="font-mono text-xs font-bold">#{trip.id}</span>
            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#0A0A0A] text-white">{trip.status?.replace(/_/g, ' ')}</span>
            {trip.trip_type && trip.trip_type !== 'normal' && (
              <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#FF8C00]/15 text-[#FF8C00]">
                {trip.trip_type === 'back_to_office' ? 'RTO' : 'W/O V'}
              </span>
            )}
          </div>
          <p className="text-sm font-bold mt-1 leading-tight">{trip.pickup_supplier_name} <span className="text-[#666] font-normal">→</span> {trip.drop_supplier_name}</p>
          <p className="text-[11px] text-[#666] mt-0.5">Start: <b className="text-[#0A0A0A]">{fmtHm(trip.started_at)}</b> · Reached: {fmtHm(trip.drop_reached_at)} · Done: {fmtHm(trip.completed_at)}</p>

          {/* Cargo */}
          {items.length > 0 && (
            <p className="text-xs mt-1" data-testid={`trip-cargo-${trip.id}`}>
              <Package size={12} className="inline mr-1 text-[#A855F7]" />
              {items.filter(it => it.name).map(it => `${it.name} ${it.qty || ''}${it.uom || ''}`).join(' · ')}
            </p>
          )}

          {/* Helpers */}
          {trip.loadmen_count > 0 && (
            <p className="text-xs mt-0.5 text-[#666]">Helpers: <b className="text-[#0A0A0A]">{trip.loadmen_count}</b>{trip.loadmen?.length ? ` (${trip.loadmen.map(l => l.name).join(', ')})` : ''}</p>
          )}

          {/* Timings table */}
          <div className="mt-2 grid grid-cols-4 gap-x-2 gap-y-1 text-[11px] bg-[#F7F7F7] p-2 rounded-sm" data-testid={`trip-timings-${trip.id}`}>
            <span className="text-[#666] uppercase tracking-widest text-[9px] font-bold">Type</span>
            <span className="text-[#666] uppercase tracking-widest text-[9px] font-bold">Pickup Wait</span>
            <span className="text-[#666] uppercase tracking-widest text-[9px] font-bold">Drive</span>
            <span className="text-[#666] uppercase tracking-widest text-[9px] font-bold">Drop Wait</span>

            <span className="font-bold">Pilot</span>
            <span>{fmtMin(trip.pickup_waiting_minutes)}</span>
            <span>{fmtMin(trip.drive_minutes)}</span>
            <span>{fmtMin(trip.drop_waiting_minutes)}</span>

            <span className="font-bold text-[#002FA7]">Google</span>
            <span className="text-[#002FA7]">{fmtMin(trip.google_pickup_waiting_min)}</span>
            <span className="text-[#002FA7]">{fmtMin(trip.google_drive_to_drop_min)}</span>
            <span className="text-[#002FA7]">{fmtMin(trip.google_drop_waiting_min)}</span>
          </div>

          {/* KMs */}
          {(trip.google_kms || trip.manual_kms) && (
            <p className="text-[11px] text-[#666] mt-1"><Path size={11} className="inline mr-1" />Google KMs: <b>{trip.google_kms || '—'}</b> · Manual: <b>{trip.manual_kms || '—'}</b></p>
          )}
        </div>

        {/* DC photo */}
        {dcUrl && (
          <a
            href={dcUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="block flex-shrink-0"
            data-testid={`trip-dc-photo-${trip.id}`}
          >
            <img src={dcUrl} alt="DC" className="w-20 h-20 object-cover border border-[#E5E5E5]" />
            <p className="text-[9px] text-center text-[#666] uppercase tracking-widest mt-0.5">DC</p>
          </a>
        )}
      </div>
    </div>
  );
}

// ----- Timeline event renderer -----
function TimelineEvent({ ev, onOpenMap }) {
  if (ev.kind === 'check_in') {
    const d = ev.data;
    return (
      <div className="border-l-4 border-[#00C853] bg-[#00C853]/5 p-3" data-testid="timeline-check-in">
        <p className="flex items-center gap-2 text-sm font-bold"><SignIn size={16} className="text-[#00C853]" />Check In · {fmtHm(d.time)}</p>
        <p className="text-xs mt-1">
          Halt: <b>{d.halt_name || '—'}</b>
          {d.inside_geofence ? (
            <span className="ml-2 text-[#00C853]">✓ Inside fence {d.soft_match ? '(within 200m)' : ''}</span>
          ) : (
            <span className="ml-2 text-[#FF3333]" data-testid="outside-fence-warning">
              <Warning size={11} weight="fill" className="inline mr-1" />
              {d.reason === 'no_gps' ? 'GPS missing at check-in' : d.reason === 'no_halt_assigned' ? 'No halt assigned' : d.reason === 'no_geofence_defined' ? 'No fence defined' : `Outside fence (${d.distance_m}m away)`}
            </span>
          )}
        </p>
        {(d.lat != null && d.lng != null) && (
          <a href={`https://www.google.com/maps/search/?api=1&query=${d.lat},${d.lng}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#002FA7] underline" onClick={e => e.stopPropagation()}>
            GPS: {d.lat.toFixed(5)}, {d.lng.toFixed(5)}
          </a>
        )}
      </div>
    );
  }
  if (ev.kind === 'check_out') {
    const d = ev.data;
    return (
      <div className="border-l-4 border-[#FF3333] bg-[#FF3333]/5 p-3" data-testid="timeline-check-out">
        <p className="flex items-center gap-2 text-sm font-bold"><SignOut size={16} className="text-[#FF3333]" />Check Out · {fmtHm(d.time)}</p>
        <p className="text-xs mt-1">
          Halt: <b>{d.halt_name || '—'}</b>
          {d.inside_geofence ? (
            <span className="ml-2 text-[#00C853]">✓ Inside fence</span>
          ) : (
            <span className="ml-2 text-[#FF3333]">⚠ Outside fence{d.distance_m ? ` (${d.distance_m}m away)` : ''}</span>
          )}
        </p>
      </div>
    );
  }
  if (ev.kind === 'trip') {
    return (
      <div data-testid={`timeline-trip-${ev.data.id}`}>
        <TripCard trip={ev.data} onOpenMap={onOpenMap} />
        {ev.data.pool_children?.map(c => (
          <div key={c.id} className="mt-2"><TripCard trip={c} onOpenMap={onOpenMap} isChild /></div>
        ))}
      </div>
    );
  }
  if (ev.kind === 'break') {
    const d = ev.data;
    return (
      <div className="border-l-4 border-[#FF8C00] bg-[#FF8C00]/5 p-3" data-testid={`timeline-break-${d.id}`}>
        <p className="flex items-center gap-2 text-sm font-bold">
          <Coffee size={16} className="text-[#FF8C00]" />Break ({d.break_type || 'break'}) · {fmtHm(d.start_time)} → {fmtHm(d.end_time)}
        </p>
        <p className="text-xs mt-1 text-[#666]">Duration: <b className="text-[#0A0A0A]">{fmtMin(d.duration_minutes)}</b>{d.excess_minutes > 0 && <span className="text-[#FF3333] ml-2">+{d.excess_minutes}m over</span>}</p>
      </div>
    );
  }
  if (ev.kind === 'non_productive') {
    const d = ev.data;
    return (
      <div className="border-l-4 border-[#A855F7] bg-[#A855F7]/5 p-3" data-testid={`timeline-np-${d.id}`}>
        <p className="flex items-center gap-2 text-sm font-bold">
          <Timer size={16} className="text-[#A855F7]" />
          {d.type === 'back_to_office' ? 'Back to Office' : d.type === 'move_without_vehicle' ? 'Move w/o Vehicle' : d.type} · {fmtHm(d.start_time)}{d.end_time ? ` → ${fmtHm(d.end_time)}` : ' (ongoing)'}
        </p>
        {d.trip_id && <p className="text-[10px] text-[#666] mt-1">Trip ref: #{d.trip_id}</p>}
      </div>
    );
  }
  if (ev.kind === 'idle') {
    const d = ev.data;
    const bgs = { amber: 'border-[#FF8C00] bg-[#FF8C00]/5 text-[#FF8C00]', red: 'border-[#FF3333] bg-[#FF3333]/5 text-[#FF3333]' };
    return (
      <div className={`border-l-4 px-3 py-2 ${bgs[d.severity]}`} data-testid={`timeline-idle-${ev.at}`}>
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest"><Clock size={12} weight="bold" />Idle {fmtMin(d.minutes)}</p>
      </div>
    );
  }
  return null;
}

// ----- Main page -----
export default function DailyReport() {
  const [pilots, setPilots] = useState([]);
  const [date, setDate] = useState(todayStr());
  const [pilotId, setPilotId] = useState('');
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [mapTripId, setMapTripId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/pilots');
        setPilots((data || []).filter(p => p.status === 'active'));
      } catch { /* ignore */ }
    })();
  }, []);

  const handleGenerate = async () => {
    if (!pilotId || !date) return;
    setLoading(true);
    setReport(null);
    try {
      const { data } = await api.get('/admin/daily-report', { params: { pilot_id: pilotId, date } });
      setReport(data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => window.print();

  const handleExportCsv = () => {
    if (!report) return;
    const rows = [['Time', 'Type', 'Detail', 'Pilot wait/drive/drop (min)', 'Google wait/drive/drop (min)', 'KMs (G/M)', 'Notes']];
    for (const ev of report.timeline) {
      const d = ev.data;
      if (ev.kind === 'check_in')      rows.push([fmtHm(d.time), 'Check In',  `Halt: ${d.halt_name || '-'}`, '', '', '', d.inside_geofence ? 'Inside fence' : `Outside fence (${d.distance_m || '?'}m)`]);
      else if (ev.kind === 'check_out') rows.push([fmtHm(d.time), 'Check Out', `Halt: ${d.halt_name || '-'}`, '', '', '', d.inside_geofence ? 'Inside fence' : `Outside fence (${d.distance_m || '?'}m)`]);
      else if (ev.kind === 'trip')      rows.push([
        fmtHm(d.started_at), `Trip #${d.id}`, `${d.pickup_supplier_name} → ${d.drop_supplier_name}`,
        `${d.pickup_waiting_minutes ?? '-'} / ${d.drive_minutes ?? '-'} / ${d.drop_waiting_minutes ?? '-'}`,
        `${d.google_pickup_waiting_min ?? '-'} / ${d.google_drive_to_drop_min ?? '-'} / ${d.google_drop_waiting_min ?? '-'}`,
        `${d.google_kms ?? '-'} / ${d.manual_kms ?? '-'}`,
        `Helpers ${d.loadmen_count || 0}`,
      ]);
      else if (ev.kind === 'break')         rows.push([fmtHm(d.start_time), 'Break',          d.break_type || 'break',      '', '', '', `Duration ${d.duration_minutes}m`]);
      else if (ev.kind === 'non_productive') rows.push([fmtHm(d.start_time), 'Non-Productive', d.type,                       '', '', '', d.trip_id ? `Trip #${d.trip_id}` : '']);
      else if (ev.kind === 'idle')          rows.push([fmtHm(ev.at),         'Idle',           `${Math.round(d.minutes)} min gap`, '', '', '', d.severity]);
    }
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `daily-report-${report.pilot.name?.replace(/\s+/g, '-')}-${report.date}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const pilotName = useMemo(() => pilots.find(p => p.id === pilotId)?.name || '', [pilots, pilotId]);

  return (
    <div className="space-y-5 print:space-y-3" data-testid="daily-report-page">
      {mapTripId && <TripRouteModal tripId={mapTripId} onClose={() => setMapTripId(null)} />}

      <div className="print:hidden">
        <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-[#0A0A0A]">Daily Report</h1>
        <p className="text-[#666] text-sm mt-1">Pick a pilot and date to see their entire day — check-in, every trip, breaks, idle gaps, and check-out.</p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E5E5] p-4 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest">Date</Label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="rounded-none" data-testid="daily-date-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Pilot</Label>
            <Select value={pilotId} onValueChange={setPilotId}>
              <SelectTrigger className="rounded-none" data-testid="daily-pilot-select"><SelectValue placeholder="Choose pilot" /></SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {pilots.map(p => (
                  <SelectItem key={p.id} value={p.id} data-testid={`daily-pilot-${p.id}`}>
                    {p.name}{p.emp_id ? ` (${p.emp_id})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex items-end gap-2 justify-end">
            <Button onClick={handleGenerate} disabled={!pilotId || loading} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-none" data-testid="daily-generate-btn">
              {loading ? 'Loading…' : 'Generate'}
            </Button>
            {report && (
              <>
                <Button variant="outline" onClick={handlePrint} className="rounded-none" data-testid="daily-print-btn"><Printer size={14} weight="bold" className="mr-1" />Print</Button>
                <Button variant="outline" onClick={handleExportCsv} className="rounded-none" data-testid="daily-csv-btn"><FileCsv size={14} weight="bold" className="mr-1" />CSV</Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Report body */}
      {!report && !loading && (
        <div className="text-center py-16 text-[#666] text-sm border border-dashed border-[#E5E5E5] bg-white">
          Choose a date and pilot, then click <b>Generate</b> to see the day's activity timeline.
        </div>
      )}

      {loading && (
        <div className="text-center py-16 text-[#666] text-sm">Loading report…</div>
      )}

      {report && (
        <>
          {/* Header card */}
          <div className="bg-[#0A0A0A] text-white p-5" data-testid="daily-report-header">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2"><User size={18} /><span className="font-bold text-lg">{report.pilot.name}</span>{report.pilot.emp_id && <span className="text-xs text-[#999]">({report.pilot.emp_id})</span>}</div>
              <span className="text-[#666]">·</span>
              <div className="flex items-center gap-2 text-sm"><Calendar size={14} />{new Date(report.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
              {report.vehicle && (
                <>
                  <span className="text-[#666]">·</span>
                  <div className="flex items-center gap-2 text-sm">
                    <Truck size={14} />
                    <span className="font-mono font-bold">{report.vehicle.vehicle_number || '—'}</span>
                    {report.vehicle.vehicle_type && <span className="text-xs text-[#999]">· {report.vehicle.vehicle_type}</span>}
                    {report.vehicle.body_type && <span className="text-xs text-[#999]">· {report.vehicle.body_type}</span>}
                  </div>
                </>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 text-center">
              <div><p className="text-2xl font-black text-[#00C853]" data-testid="summary-trips">{report.summary.trip_count}</p><p className="text-[10px] uppercase tracking-widest text-[#999]">Trips</p></div>
              <div><p className="text-2xl font-black text-[#A855F7]" data-testid="summary-pools">{report.summary.pool_child_count}</p><p className="text-[10px] uppercase tracking-widest text-[#999]">Pooled</p></div>
              <div><p className="text-2xl font-black text-[#FF8C00]" data-testid="summary-breaks">{report.summary.break_count}</p><p className="text-[10px] uppercase tracking-widest text-[#999]">Breaks ({Math.round(report.summary.break_total_min)}m)</p></div>
              <div><p className="text-2xl font-black text-[#FF3333]" data-testid="summary-np">{report.summary.non_productive_count}</p><p className="text-[10px] uppercase tracking-widest text-[#999]">Non-Prod</p></div>
              <div><p className="text-2xl font-black text-[#002FA7]" data-testid="summary-pending">{report.summary.pending_count}</p><p className="text-[10px] uppercase tracking-widest text-[#999]">Pending (EOD)</p></div>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <h2 className="text-lg font-bold uppercase tracking-widest text-[#666] mb-2">Day Timeline</h2>
            {report.timeline.length === 0 ? (
              <p className="text-center text-[#999] text-sm py-8 border border-dashed border-[#E5E5E5]">No activity recorded for this date.</p>
            ) : (
              <div className="space-y-2" data-testid="daily-timeline">
                {report.timeline.map((ev, i) => (
                  <TimelineEvent key={`${ev.kind}-${i}`} ev={ev} onOpenMap={setMapTripId} />
                ))}
              </div>
            )}
          </div>

          {/* Pending trips */}
          {report.pending_trips.length > 0 && (
            <div data-testid="daily-pending-trips">
              <h2 className="text-lg font-bold uppercase tracking-widest text-[#666] mb-2 flex items-center gap-2"><FlagCheckered size={16} />Pending Trips (in hand at EOD)</h2>
              <div className="space-y-2">
                {report.pending_trips.map(p => (
                  <div key={p.id} className="border border-[#FFD700] bg-[#FFD700]/10 p-3 text-sm" data-testid={`pending-trip-${p.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <span className="font-mono text-xs font-bold mr-2">#{p.id}</span>
                        <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#0A0A0A] text-white">{p.status?.replace(/_/g, ' ')}</span>
                      </div>
                      <span className="text-xs text-[#666]">{fmtTime(p.created_at)}</span>
                    </div>
                    <p className="mt-1 leading-tight">{p.pickup_supplier_name} → {p.drop_supplier_name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
